import type { ContentBuilderFieldColor } from '@front-platform/content-builder';

import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';

const COLOR_FIELD_TYPES = new Set(['hex', 'palette', 'default']);

export type BlockConfigWithCssColors<T> = {
  [K in keyof T]: T[K] extends ContentBuilderFieldColor
    ? string
    : T[K] extends Record<string, unknown>
      ? BlockConfigWithCssColors<T[K]>
      : T[K];
};

function isContentBuilderFieldColor(value: unknown): value is ContentBuilderFieldColor {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  if (typeof candidate.type !== 'string' || typeof candidate.value !== 'string') {
    return false;
  }

  return COLOR_FIELD_TYPES.has(candidate.type.toLowerCase());
}

export function resolveBlockConfigColors<T extends Record<string, unknown>>(
  config: T,
): BlockConfigWithCssColors<T> {
  const resolved: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(config)) {
    if (isContentBuilderFieldColor(value)) {
      resolved[key] = fromContentBuilderColor(value);
      continue;
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      resolved[key] = resolveBlockConfigColors(value as Record<string, unknown>);
      continue;
    }

    resolved[key] = value;
  }

  return resolved as BlockConfigWithCssColors<T>;
}
